import * as React from 'react';
import { useEffect, useRef, useState } from 'react';
import { LabelWrapperProps } from '../label-wrapper';
import { Dropdown } from '../dropdown';
import { SelectMenu } from '../select-menu';
import { SelectOption } from '../../common';
import { Input } from '../input';

export interface SimpleSelectProps<T> extends LabelWrapperProps {
  onSelect?: (val: SelectOption<T>) => void;
  options: SelectOption<T>[];
  readOnly?: boolean;
  selected?: SelectOption<T>;
}

export function SimpleSelect<T>(props: React.PropsWithChildren<SimpleSelectProps<T>>) {
  const {
    onSelect,
    selected,
    caption,
    label,
    hasError,
    disabled,
    required,
    right,
    options,
    readOnly,
    small,
  } = props;
  const [getSelIndex, setSelIndex] = useState<number | null>(null);
  useEffect(() => {
    if (!selected) {
      return;
    }
    const find = options.findIndex(f =>
      f.uid && selected.uid ? f.uid === selected.uid : f.label === selected.label
    );
    if (find < 0) {
      return;
    }
    setSelIndex(find);
  }, [selected, options]);
  const [getOpen, setOpen] = useState<boolean>(false);
  const refWrapper = useRef<HTMLDivElement>(null);

  const emitSelection = (item: SelectOption<T> | undefined): void => {
    if (!item) {
      return;
    }
    if (onSelect) {
      onSelect(item);
    }
    setOpen(false);
  };
  const selectDown = (): void => {
    if (getSelIndex === null) {
      setSelIndex(0);
      return;
    }
    let newIndex = getSelIndex + 1;
    if (newIndex > options.length - 1) {
      newIndex = 0;
    }
    setSelIndex(newIndex);
  };
  const selectUp = (): void => {
    if (getSelIndex === null) {
      setSelIndex(options.length - 1);
      return;
    }
    let newIndex = getSelIndex - 1;
    if (newIndex < 0) {
      newIndex = options.length - 1;
    }
    setSelIndex(newIndex);
  };
  const onKeyDown = (event: React.KeyboardEvent) => {
    if (readOnly) {
      return;
    }
    switch (event.keyCode) {
      case 40: // down
        selectDown();
        setOpen(true);
        break;
      case 38: // up
        selectUp();
        setOpen(true);
        break;
      case 27: // escape
        setOpen(false);
        break;
      case 13: // enter
        if (getSelIndex !== null) {
          emitSelection(options[getSelIndex]);
        }
        break;
      case 9: // tab
        break;
      default: {
        const filterPropositions = options
          .map((m, i) => ({ ...m, originIndex: i }))
          .filter(f =>
            f.label.toLowerCase().startsWith(String.fromCharCode(event.keyCode).toLowerCase())
          );
        if (!filterPropositions.length) {
          return;
        }
        const filterIndex = filterPropositions.findIndex(fi => fi.originIndex === getSelIndex);
        if (filterIndex > -1) {
          let newIndex = filterIndex + 1;
          if (newIndex > filterPropositions.length - 1) {
            newIndex = 0;
          }
          setSelIndex(filterPropositions[newIndex].originIndex);
        } else {
          setSelIndex(filterPropositions[0].originIndex);
        }
      }
    }
  };
  return (
    <div style={{ position: 'relative', width: '100%' }} ref={refWrapper}>
      <Input
        {...{ caption, label, hasError, disabled, required, right, small }}
        onChange={() => {}}
        value={selected?.label || ''}
        autoComplete="off"
        onFocus={readOnly ? undefined : () => setOpen(true)}
        onKeyDown={onKeyDown}
        readOnly={readOnly}
        icon={readOnly ? undefined : 'down'}
      />
      {refWrapper && (
        <Dropdown
          wrapperRef={refWrapper}
          onClose={() => setOpen(false)}
          fulfilled
          show={getOpen}
          offset={-12}
        >
          <SelectMenu<T>
            onSelect={emitSelection}
            selected={getSelIndex !== null ? options[getSelIndex] : undefined}
            items={options}
          />
        </Dropdown>
      )}
    </div>
  );
}
